import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export type ConversationType = 'CLIENT_ADMIN' | 'ADMIN_PRO' | 'CLIENT_PRO';

export interface Participant {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: 'CLIENT' | 'PROFESSIONAL' | 'ADMIN';
}

export interface MessageMetadata {
  type:
    | 'text'
    | 'assignment_request'
    | 'assignment_response'
    | 'visit_scheduled'
    | 'visit_slot_request'
    | 'visit_slot_proposal'
    | 'visit_slot_confirmed';
  contactRequestId?: string;
  action?: 'accepted' | 'rejected';
  clientName?: string;
  propertyTitle?: string;
  propertyAddress?: string;
  professionalMessage?: string;
  visitDate?: string;
  visitNotes?: string;
  visitDuration?: number;
  slotId?: string;
  proposedBy?: 'admin' | 'client' | 'professional';
}

export interface Message {
  id: string;
  conversationId: string;
  senderId: string;
  content: string;
  metadata?: MessageMetadata | null;
  readAt: string | null;
  createdAt: string;
  sender: {
    id: string;
    firstName: string;
    lastName: string;
    role: string;
  };
}

export interface Conversation {
  id: string;
  type: ConversationType;
  contactRequestId: string | null;
  otherParticipant: Participant;
  lastMessage: Message | null;
  unreadCount: number;
  lastMessageAt: string | null;
  createdAt: string;
}

export interface ConversationDetail {
  conversation: {
    id: string;
    type: ConversationType;
    contactRequestId: string | null;
    otherParticipant: Participant;
    createdAt: string;
  };
  messages: Message[];
  total: number;
  skip: number;
  take: number;
}

export interface CreateConversationPayload {
  type: ConversationType;
  participantId?: string;
  contactRequestId?: string;
  initialMessage?: string;
}

export interface SendMessagePayload {
  conversationId: string;
  content: string;
}

/**
 * Messaging Service - Handles all messaging API calls
 */
export const messagingService = {
  /**
   * Get all conversations for the current user
   */
  getConversations: async (skip = 0, take = 20): Promise<{
    conversations: Conversation[];
    total: number;
  }> => {
    const response = await axios.get(`${API}/messaging/conversations`, {
      params: { skip, take },
    });
    return response.data;
  },

  /**
   * Get a specific conversation with messages
   */
  getConversation: async (
    conversationId: string,
    skip = 0,
    take = 50
  ): Promise<ConversationDetail> => {
    const response = await axios.get(
      `${API}/messaging/conversations/${conversationId}`,
      { params: { skip, take } }
    );
    return response.data;
  },

  /**
   * Create a new conversation or get existing one
   */
  createConversation: async (
    payload: CreateConversationPayload
  ): Promise<Conversation> => {
    const response = await axios.post(`${API}/messaging/conversations`, payload);
    return response.data;
  },

  /**
   * Send a message in a conversation
   */
  sendMessage: async (payload: SendMessagePayload): Promise<Message> => {
    const response = await axios.post(`${API}/messaging/messages`, payload);
    return response.data;
  },

  /**
   * Mark messages as read in a conversation
   */
  markAsRead: async (conversationId: string): Promise<void> => {
    await axios.post(`${API}/messaging/conversations/${conversationId}/read`);
  },

  /**
   * Get total unread message count
   */
  getUnreadCount: async (): Promise<{ unreadCount: number }> => {
    const response = await axios.get(`${API}/messaging/unread-count`);
    return response.data;
  },

  /**
   * Get conversations linked to a contact request
   */
  getConversationsForContactRequest: async (
    contactRequestId: string
  ): Promise<Conversation[]> => {
    const response = await axios.get(
      `${API}/messaging/contact-request/${contactRequestId}/conversations`
    );
    return response.data;
  },

  /**
   * Find a user by email (used to resolve seekers to registered users)
   */
  getUserByEmail: async (email: string): Promise<Participant> => {
    const response = await axios.get(
      `${API}/messaging/users/by-email/${encodeURIComponent(email)}`
    );
    return response.data;
  },

  /**
   * Respond to a lead assignment (accept or reject)
   * This is called by professionals via the messaging interface
   */
  respondToAssignment: async (
    contactRequestId: string,
    action: 'accept' | 'reject',
    message?: string
  ): Promise<{
    message: string;
    lead: any;
    conversationId: string | null;
  }> => {
    const response = await axios.post(
      `${API}/professional/leads/${contactRequestId}/respond`,
      { action, message }
    );
    return response.data;
  },
};

export default messagingService;

